import axios from 'axios';
import { LOGOUT_URL_PATH } from '../constants'

const signOut = () => {
  // get legacy token - we will have to redirect to the legacy sign out page is this has a value
  const legacyToken = getToken();

  // remove v3 auth
  document.cookie = `auth_token=; Domain=${process.env.AUTH_COOKIE_DOMAIN}; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=None;`;
  window.localStorage.removeItem('user');
  window.localStorage.removeItem('nav-collapsed');

  if (legacyToken) {
    window.history.pushState({}, '', LOGOUT_URL_PATH);
    window.location.reload();
  } else {
    // redirect to login screen
    let url = process.env.DASHBOARD_V3_URL + '/sign-in';

    if (window.location.pathname) {
      url += `?returnUrl=${window.location.pathname}`;
    }

    window.location.replace(url);
  }
}

const getToken = (name = 'token') => {
  try {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name))
      .split('=')[1];
  } catch (e) {
    return '';
  }
}

const authInterceptor = (config) => {
  // add the auth token to the request headers
  config.headers.Authorization = getToken('auth_token');

  // this is used to calculate a duration in the logError function
  config.metadata = { startTime: new Date() };

  // if token is null - sign out
  if (!config.headers.Authorization) {
    logError({
      config: {
        ...config,
        url: config.baseURL + config.url
      },
      response: {
        status: 0,
        data: {
          message: 'Empty Authorization Token - Request Cancelled'
        }
      }
    }).then(q => {
      signOut();
    });
    throw new axios.Cancel('Authorization Failed - Please refresh the browser');
  }

  return config;
}

const logError = (error) => {
  const endTime = new Date();
  const duration = endTime - error.config.metadata.startTime;
  const url = new URL(error.config.url);

  return axios({
    method: 'post',
    url: process.env.LOG_API_URL + 'error',
    data: {
      source: 'Ois.UI.Dashboard',
      sourceHost: url.pathname,
      category: 'Error',
      summary: `${duration > 28500 ? 'Timeout' : 'Duration'}: ${duration} | Method: ${error.config.method} | Url: ${url.host}${url.pathname}${url.search}` + `${error.response ? ` | Status: ${error.response.status} | Data: ${JSON.stringify(error.response.data)}` : ''}`
    },
    headers: {
      Authorization: getToken()
    }
  }).catch(e => {
    console.log(e);
    return null;
  });
}

const handleError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return {
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    return {
      status: 500,
      data: 'The request was made but no response was received'
    };
  } else if (axios.isCancel(error)) {
    return {
      status: 499,
      data: error.message
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      status: 500,
      data: error.message
    };
  }
}

const handle401Error = (error) => {
  try {
    if (axios.isCancel(error)) {
      // return error
      return Promise.reject(error);
    }

    console.log(error);

    // 401 - unauthorized user - sign out
    if ((error.response ? error.response.status : 0) === 401) {
      logError(error).then(q => {
        signOut();
      });
    } else {
      // log error to sumo
      logError(error);

      // return error
      return Promise.reject(error);
    }
  } catch (e) {
    console.log(e);
  }
  return Promise.reject(error);
}

const qaApi = axios.create({ baseURL: process.env.ADMIN_API_URL });
qaApi.interceptors.request.use(authInterceptor);
qaApi.interceptors.response.use(response => response, e => handle401Error(e));

const inspectorApi = axios.create({ baseURL: process.env.INSPECTOR_API_URL });
inspectorApi.interceptors.request.use(authInterceptor);
inspectorApi.interceptors.response.use(response => response, e => handle401Error(e));

const referenceDataApi = axios.create({ baseURL: process.env.REFERENCE_DATA_API_URL });
referenceDataApi.interceptors.request.use(authInterceptor);
referenceDataApi.interceptors.response.use(response => response, e => handle401Error(e));

const campaignApi = axios.create({ baseURL: process.env.CAMPAIGN_API_URL });
campaignApi.interceptors.request.use(authInterceptor);
campaignApi.interceptors.response.use(response => response, e => handle401Error(e));

export { getToken, handleError, qaApi, inspectorApi, referenceDataApi, campaignApi };
